import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

import styled from 'styled-components';

import { Button } from '@components/button';
import { IconProps } from '@components/icons';
import { IconAiAgent } from '@components/icons/ai-agent';
import { IconJourneyItem } from '@components/icons/journey-icon';
import { IconJourneyLogoPlain } from '@components/icons/journey-logo-plain';
import { IconPortal } from '@components/icons/portal';
import { IconTraveller } from '@components/icons/traveller';
import { Modal, hideModal, useModal } from '@components/modal';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { getScopedTranslate } from '@locale/translate';
import { theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';

import { useCurrentUser } from 'api/authentication/hooks';
import { useEditMyDetails } from 'api/users/hooks';

import aiVideo from '../../../static/introduction-videos/ai.mp4';
import introductionVideo from '../../../static/introduction-videos/introduction.mp4';
import journeysVideo from '../../../static/introduction-videos/journeys.mp4';
import portalVideo from '../../../static/introduction-videos/portal.mp4';
import travellersVideo from '../../../static/introduction-videos/travellers.mp4';

type PageType = 'introduction' | 'journeys' | 'travellers' | 'portal' | 'aiAgents';

const PAGE_ORDER: PageType[] = ['introduction', 'journeys', 'travellers', 'portal', 'aiAgents'];

const PAGE_TO_VIDEO_MAPPER: Record<PageType, string> = {
  introduction: introductionVideo,
  journeys: journeysVideo,
  travellers: travellersVideo,
  portal: portalVideo,
  aiAgents: aiVideo,
};

const PAGE_TO_ICON_MAPPER: Record<PageType, React.ComponentType<IconProps>> = {
  introduction: IconJourneyLogoPlain,
  journeys: IconJourneyItem,
  travellers: IconTraveller,
  portal: IconPortal,
  aiAgents: IconAiAgent,
};

export const useIntroductionModal = () => {
  const currentUser = useCurrentUser();
  const showModal = useModal(IntroductionModal, {
    variant: 'EXTRA_EXTRA_LARGE',
    backgroundColor: 'backgroundPrimary',
    hasBlurredAndForcedBackdrop: true,
  });
  const { pathname } = useLocation();
  const shouldShowModal = !currentUser?.hasFinishedOnboarding;

  useEffect(() => {
    if (shouldShowModal) {
      showModal();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);
};

const IntroductionModal = () => {
  const { t } = getScopedTranslate(locale);
  const [activePage, setActivePage] = useState<PageType>('introduction');

  return (
    <Modal>
      <Modal.Content>
        <Stack space={10} orientation="horizontal">
          <Stack space={2} style={{ minWidth: pxToRem(204) }}>
            {PAGE_ORDER.map((page) => (
              <SectionSelector
                key={page}
                isActive={activePage === page}
                icon={PAGE_TO_ICON_MAPPER[page]}
                text={t(page)}
              />
            ))}
          </Stack>
          <Section currentPage={activePage} setActivePage={setActivePage} />
          <VideoContainer>
            <StyledVideo autoPlay muted loop playsInline src={PAGE_TO_VIDEO_MAPPER[activePage]} />
          </VideoContainer>
        </Stack>
      </Modal.Content>
    </Modal>
  );
};

const SectionSelector: React.FC<{
  icon: React.ComponentType<IconProps>;
  text: string;
  isActive?: boolean;
}> = ({ icon: IconComponent, text, isActive = false }) => {
  return (
    <SectionSelectorContainer data-active={isActive}>
      <Stack space={2} orientation="horizontal" align="center">
        <IconComponent size={24} color={isActive ? 'elementBlue100' : 'elementPurple80'} />
        <Text font="semibold12" color={isActive ? 'elementBlue100' : 'textPrimary'} text={text} />
      </Stack>
    </SectionSelectorContainer>
  );
};

const Section = ({
  currentPage,
  setActivePage,
}: {
  currentPage: PageType;
  setActivePage: (page: PageType) => void;
}) => {
  const { t } = getScopedTranslate(locale);
  const userMutation = useEditMyDetails();
  const currentUser = useCurrentUser();

  const handleFinish = () => {
    const formData = new FormData();
    formData.append('hasFinishedOnboarding', 'true');
    userMutation.mutate(formData);
    hideModal();
    localStorage.setItem(
      `company-onboarding-location-${currentUser?.company.shortId}`,
      JSON.stringify('sidebar')
    );
  };

  const isLastPage = currentPage === PAGE_ORDER[PAGE_ORDER.length - 1];

  const handleContinue = () => {
    if (isLastPage) {
      handleFinish();
    } else {
      const currentIndex = PAGE_ORDER.indexOf(currentPage);
      setActivePage(PAGE_ORDER[currentIndex + 1]);
    }
  };

  const headerKey = `header${currentPage}` as keyof typeof locale.en;
  const subHeaderKey = `subHeader${currentPage}` as keyof typeof locale.en;

  return (
    <Stack space={4} style={{ minWidth: pxToRem(424), maxWidth: pxToRem(424) }}>
      <div>
        <Text font="bold14" color="indigo80" text={t(headerKey)} />
        <Text font="heading1" color="textPrimary" text={t(subHeaderKey)} />
      </div>
      <SectionContent pageType={currentPage} />
      <Stack orientation="horizontal">
        <Button
          onClick={handleContinue}
          text={t(isLastPage ? 'finish' : 'core.continue')}
          variant="primary-blue"
        />
      </Stack>
    </Stack>
  );
};

const BulletList: React.FC<{ items: string[] }> = ({ items }) => {
  return (
    <Stack space={1}>
      {items.map((item, index) => (
        <Text font="regular14" color="textPrimary" text={item} key={index} />
      ))}
    </Stack>
  );
};

const SectionContent = ({ pageType }: { pageType: PageType }) => {
  const { t } = getScopedTranslate(locale);

  const pageContent: Record<
    PageType,
    {
      text: string;
      header?: string;
      bullets?: string[];
      footer?: string;
    }
  > = {
    introduction: {
      text: t('textIntroduction'),
    },
    journeys: {
      text: t('textJourneys'),
      header: t('textJourneysHeader'),
      bullets: [
        t('textJourneysBullet1'),
        t('textJourneysBullet2'),
        t('textJourneysBullet3'),
        t('textJourneysBullet4'),
      ],
    },
    travellers: {
      text: t('textTravellers'),
      header: t('textTravellersHeader'),
      bullets: [
        t('textTravellersBullet1'),
        t('textTravellersBullet2'),
        t('textTravellersBullet3'),
        t('textTravellersBullet4'),
      ],
      footer: t('textTravellersFooter'),
    },
    portal: {
      text: t('textPortal'),
      header: t('textPortalHeader'),
      bullets: [t('textPortalBullet1'), t('textPortalBullet2'), t('textPortalBullet3')],
      footer: t('textPortalFooter'),
    },
    aiAgents: {
      text: t('textAiAgents'),
      header: t('textAiAgentsHeader'),
      bullets: [t('textAiAgentsBullet1'), t('textAiAgentsBullet2'), t('textAiAgentsBullet3')],
      footer: t('textAiAgentsFooter'),
    },
  };

  const content = pageContent[pageType];

  if (pageType === 'introduction') {
    return <Text font="regular14" color="textPrimary" text={content.text!} />;
  }

  return (
    <Stack space={1}>
      {content.text && <Text font="regular14" color="textPrimary" text={content.text} />}
      {content.header && <Text font="bold14" color="textPrimary" text={content.header} />}
      {content.bullets && <BulletList items={content.bullets} />}
      {content.footer && (
        <Text
          font="regular14"
          color="textPrimary"
          text={content.footer}
          style={{ marginTop: pxToRem(4) }}
        />
      )}
    </Stack>
  );
};

const SectionSelectorContainer = styled.div({
  borderRadius: pxToRem(8),
  color: theme.color.textPrimary,
  transition: 'all 150ms ease-out',
  transitionProperty: 'background, border-color',
  '&[data-active="true"]': {
    padding: `calc(${pxToRem(16)} - ${pxToRem(1)})`,
    border: `${pxToRem(2)} solid ${theme.color.elementBlue100}`,
    backgroundColor: theme.color.backgroundLightDark,
  },
  '&[data-active="false"]': {
    border: `${pxToRem(1)} solid ${theme.color.border10}`,
    padding: pxToRem(16),
  },
});

const VideoContainer = styled.div({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  width: '100%',
  height: pxToRem(488),
  borderRadius: pxToRem(8),
  overflow: 'hidden',
});

const StyledVideo = styled.video({
  width: '100%',
  height: '100%',
  objectFit: 'cover',
  borderRadius: pxToRem(8),
});

const locale = {
  en: {
    introduction: 'Introduction',
    journeys: 'Journeys',
    travellers: 'Travellers',
    portal: 'Company Portal',
    aiAgents: 'AI Agents',

    headerintroduction: "Let's get started",
    subHeaderintroduction: 'Hi, Welcome to Journeys 🎉',
    textIntroduction:
      "We're excited to have you here! Dive in, explore the powerful AI-driven features, and discover how Journeys can help you simplify tasks, save time, and add value to your workflow.\n\nLet's get started by showing you around!",

    headerjourneys: 'Build Once, Automate Forever',
    subHeaderjourneys: 'Journeys',
    textJourneys:
      "Automate manual tasks and bring your workflows to life with our intuitive Journey Builder. It's all about saving time, reducing effort, and making things flow smoothly:\n\n",
    textJourneysHeader: "Here's what you can do:",
    textJourneysBullet1:
      '🛠️ Build custom workflows in just a few clicks — simple, powerful, and easy to use',
    textJourneysBullet2:
      '📝 Add smart forms directly into your workflows using our flexible form builder',
    textJourneysBullet3:
      '📄 Configure documents, automate e-signing, and streamline your entire process from start to finish',
    textJourneysBullet4:
      '🔌 Connect with your favorite tools — from CRMs to HR platforms — or set up custom integrations with almost any third-party software',

    headertravellers: 'All Aboard!',
    subHeadertravellers: 'Travellers',
    textTravellers:
      "Once your journey is set up, it's time to send people through it — that's where Travellers come in. Travellers are the individuals who move through your workflows to complete tasks and provide information.\n\n",
    textTravellersHeader: "Here's a few examples what Travellers can do along the way:",
    textTravellersBullet1: '📝 Fill out forms',
    textTravellersBullet2: '✍️ Sign documents',
    textTravellersBullet3: '📊 Provide data and feedback',
    textTravellersBullet4: '✅ Complete custom actions',
    textTravellersFooter:
      'Track everything in real time — from individual progress to overall status — all in one place.',

    headerportal: 'Your Portal, Your Rules',
    subHeaderportal: 'Company portal',
    textPortal:
      'Empower your team to get started on their own. With the Company Portal, employees can join journeys themselves — no manual work needed.\n\n',
    textPortalHeader: "Here's what makes it powerful:",
    textPortalBullet1: '🔄 Enable self-service to save time and reduce manual work',
    textPortalBullet2: '🚀 Let employees start relevant workflows whenever they need to',
    textPortalBullet3: "🎨 Customize the portal to match your company's branding and vibe",
    textPortalFooter: 'Build once, and let your people handle the rest.',

    headeraiAgents: 'Supercharge Your Workflows with AI',
    subHeaderaiAgents: 'Add AI Agents to your workflow',
    textAiAgents:
      'Bring intelligence, automation, and precision to your processes with AI Agents — built to boost productivity while keeping things secure and compliant.\n\n',
    textAiAgentsHeader: "Here's how they help:",
    textAiAgentsBullet1: "⚙️ Add AI steps exactly where they're needed in your workflows",
    textAiAgentsBullet2:
      '🧠 Build custom Agents to handle tasks, pull from knowledge bases, and generate outputs',
    textAiAgentsBullet3: '📊 Maintain full visibility and control at every stage',
    textAiAgentsFooter: 'Use them when and where it matters — and watch your workflows level up.',

    finish: "I'm ready, let's go!",
    'core.continue': 'Continue',
  },
};
